// ============================================================
// PDF Generator Utility — Assessment Results & Candidate Reports
// ============================================================

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const COLORS = {
    primary: [99, 102, 241] as [number, number, number],       // #6366f1
    success: [16, 185, 129] as [number, number, number],       // #10b981
    danger: [239, 68, 68] as [number, number, number],         // #ef4444
    warning: [245, 158, 11] as [number, number, number],       // #f59e0b
    gray: [107, 114, 128] as [number, number, number],         // #6b7280
    lightGray: [243, 244, 246] as [number, number, number],    // #f3f4f6
    white: [255, 255, 255] as [number, number, number],
    dark: [17, 24, 39] as [number, number, number],            // #111827
};

function addHeader(doc: jsPDF, title: string, subtitle?: string) {
    const pageWidth = doc.internal.pageSize.getWidth();

    // Purple header bar
    doc.setFillColor(...COLORS.primary);
    doc.rect(0, 0, pageWidth, 32, 'F');

    // HireHub branding
    doc.setTextColor(...COLORS.white);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    doc.text('HireHub', 14, 16);

    // Title
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(title, 14, 26);

    // Date
    doc.setFontSize(8);
    doc.text(`Generated: ${new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}`, pageWidth - 14, 16, { align: 'right' });

    if (subtitle) {
        doc.setFontSize(8);
        doc.text(subtitle, pageWidth - 14, 22, { align: 'right' });
    }

    // Reset text color
    doc.setTextColor(...COLORS.dark);

    return 38; // y position after header
}

function addSectionTitle(doc: jsPDF, y: number, title: string, icon?: string): number {
    const pageWidth = doc.internal.pageSize.getWidth();
    if (y > doc.internal.pageSize.getHeight() - 30) {
        doc.addPage();
        y = 20;
    }

    doc.setFillColor(...COLORS.lightGray);
    doc.roundedRect(14, y, pageWidth - 28, 10, 2, 2, 'F');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(...COLORS.primary);
    doc.text(`${icon ? icon + ' ' : ''}${title}`, 18, y + 7);
    doc.setTextColor(...COLORS.dark);

    return y + 16;
}

function addInfoRow(doc: jsPDF, y: number, label: string, value: string, x?: number): number {
    if (y > doc.internal.pageSize.getHeight() - 20) {
        doc.addPage();
        y = 20;
    }
    const startX = x || 18;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...COLORS.gray);
    doc.text(label + ':', startX, y);
    doc.setTextColor(...COLORS.dark);
    doc.setFont('helvetica', 'bold');
    doc.text(String(value || '—'), startX + 55, y);
    doc.setFont('helvetica', 'normal');
    return y + 7;
}

function getScoreColor(score: number): [number, number, number] {
    if (score >= 70) return COLORS.success;
    if (score >= 40) return COLORS.warning;
    return COLORS.danger;
}

// ── Single Candidate PDF ─────────────────────────────────────

export function generateCandidateResultPDF(candidate: any) {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();

    let y = addHeader(doc, 'Candidate Report', candidate.job?.title || '');

    // ── Candidate Information ────────────────────────────────
    y = addSectionTitle(doc, y, 'Candidate Information');
    y = addInfoRow(doc, y, 'Name', candidate.name || 'Unknown');
    y = addInfoRow(doc, y, 'Email', candidate.email || '—');
    y = addInfoRow(doc, y, 'Phone', candidate.phone || '—');
    y = addInfoRow(doc, y, 'Job Title', candidate.job?.title || '—');
    y = addInfoRow(doc, y, 'Current Stage', typeof candidate.stage === 'object' ? candidate.stage?.name : candidate.stage || '—');
    y = addInfoRow(doc, y, 'Applied', candidate.appliedAt ? new Date(candidate.appliedAt).toLocaleDateString('en-IN') : '—');
    y += 4;

    // ── Application Form Data ────────────────────────────────
    const submissions = candidate.formSubmissions || candidate.submissions || [];
    if (submissions.length > 0) {
        y = addSectionTitle(doc, y, 'Application Form Data');

        for (const sub of submissions) {
            if (sub.formName) {
                doc.setFont('helvetica', 'bold');
                doc.setFontSize(9);
                doc.setTextColor(...COLORS.primary);
                doc.text(sub.formName, 18, y);
                doc.setTextColor(...COLORS.dark);
                y += 7;
            }

            const data = sub.data || {};
            const entries = Object.entries(data).filter(([key]) =>
                !['name', 'email', 'phone', 'selectedJobId'].includes(key)
            );

            for (const [key, value] of entries) {
                if (y > doc.internal.pageSize.getHeight() - 20) {
                    doc.addPage();
                    y = 20;
                }

                if (Array.isArray(value)) {
                    // Table data (qualifications, experience, etc.)
                    if (value.length > 0 && typeof value[0] === 'object') {
                        doc.setFont('helvetica', 'bold');
                        doc.setFontSize(8);
                        doc.text(key, 18, y);
                        y += 4;

                        const cols = Object.keys(value[0]);
                        const rows = value.map(row => cols.map(c => String((row as any)[c] || '')));

                        autoTable(doc, {
                            startY: y,
                            head: [cols],
                            body: rows,
                            margin: { left: 18, right: 18 },
                            styles: { fontSize: 7, cellPadding: 2 },
                            headStyles: {
                                fillColor: COLORS.primary,
                                textColor: COLORS.white,
                                fontStyle: 'bold',
                            },
                            alternateRowStyles: { fillColor: [249, 250, 251] },
                        });

                        y = (doc as any).lastAutoTable.finalY + 6;
                    } else {
                        // Simple array (e.g. checkbox values)
                        y = addInfoRow(doc, y, key, value.join(', '));
                    }
                } else if (typeof value === 'object' && value !== null) {
                    y = addInfoRow(doc, y, key, JSON.stringify(value));
                } else {
                    y = addInfoRow(doc, y, key, String(value || ''));
                }
            }
            y += 4;
        }
    }

    // ── Assessment Results ────────────────────────────────────
    const assessmentResults = candidate.assessmentResults || candidate.attempts || [];
    if (assessmentResults.length > 0) {
        y = addSectionTitle(doc, y, 'Assessment Results');

        for (const result of assessmentResults) {
            if (y > doc.internal.pageSize.getHeight() - 40) {
                doc.addPage();
                y = 20;
            }

            const scorePercent = result.scorePercent ?? 0;
            const scoreColor = getScoreColor(scorePercent);

            // Assessment name + score card
            doc.setFillColor(249, 250, 251);
            doc.roundedRect(18, y, pageWidth - 36, 22, 2, 2, 'F');

            doc.setFont('helvetica', 'bold');
            doc.setFontSize(10);
            doc.text(result.assessmentTitle || result.assessmentName || 'Assessment', 22, y + 8);

            doc.setFontSize(8);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(...COLORS.gray);
            doc.text(
                `Status: ${result.status || '—'} | Completed: ${result.completedAt ? new Date(result.completedAt).toLocaleDateString('en-IN') : '—'}`,
                22, y + 16
            );

            // Score badge
            doc.setFillColor(...scoreColor);
            doc.roundedRect(pageWidth - 56, y + 3, 34, 16, 3, 3, 'F');
            doc.setTextColor(...COLORS.white);
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(12);
            doc.text(`${scorePercent.toFixed(0)}%`, pageWidth - 39, y + 14, { align: 'center' });
            doc.setTextColor(...COLORS.dark);

            y += 28;

            // MCQ breakdown
            if (result.breakdown?.mcqTotal > 0) {
                doc.setFontSize(8);
                doc.setFont('helvetica', 'normal');
                doc.setTextColor(...COLORS.gray);
                doc.text(`MCQ: ${result.breakdown.mcqCorrect}/${result.breakdown.mcqTotal} correct (${result.mcqScore != null ? result.mcqScore.toFixed(0) : '—'}%)`, 22, y);
                y += 5;
            }
            if (result.breakdown?.subjectiveTotal > 0) {
                doc.setFontSize(8);
                doc.text(`Subjective: ${result.subjectiveScore != null ? result.subjectiveScore.toFixed(0) : '—'}%`, 22, y);
                y += 5;
            }
            if (result.timeUsedSeconds) {
                doc.setFontSize(8);
                doc.text(`Time Used: ${Math.round(result.timeUsedSeconds / 60)} min`, 22, y);
                y += 5;
            }
            doc.setTextColor(...COLORS.dark);

            // Per-question responses table
            const responses = result.responses || [];
            if (responses.length > 0) {
                y += 2;

                const tableRows = responses.map((resp: any, i: number) => {
                    let answerText = '';
                    let status = '';

                    if (resp.questionType === 'mcq') {
                        const selected = resp.answer?.selectedOptionIds || [];
                        const correct = resp.correctAnswer?.correctOptionIds || [];
                        const options = resp.correctAnswer?.options || [];

                        if (options.length > 0) {
                            const selectedTexts = options
                                .filter((o: any) => selected.includes(o.id))
                                .map((o: any) => o.text);
                            answerText = selectedTexts.join(', ') || 'No answer';
                        } else {
                            answerText = selected.length > 0 ? `Selected: ${selected.length} option(s)` : 'No answer';
                        }

                        if (correct.length > 0) {
                            const isCorrect = JSON.stringify([...selected].sort()) === JSON.stringify([...correct].sort());
                            status = isCorrect ? '✓ Correct' : '✗ Incorrect';
                        } else {
                            status = '—';
                        }
                    } else if (resp.questionType === 'speaking') {
                        answerText = resp.answer?.videoKey ? 'Video recorded' : 'No recording';
                        status = resp.subjectiveScore ? `${resp.subjectiveScore.score}/${resp.subjectiveScore.maxScore}` : '—';
                    } else {
                        const answer = resp.answer;
                        answerText = typeof answer === 'object'
                            ? (answer?.code || answer?.text || answer?.answer || 'Submitted')
                            : String(answer || 'No answer');
                        if (answerText.length > 120) answerText = answerText.substring(0, 117) + '...';
                        status = resp.subjectiveScore ? `${resp.subjectiveScore.score}/${resp.subjectiveScore.maxScore}` : '—';
                    }

                    const qText = resp.questionText || `${resp.questionType} question`;
                    const displayQ = qText.length > 70 ? qText.substring(0, 67) + '...' : qText;

                    return [`Q${i + 1}`, displayQ, resp.questionType?.toUpperCase() || '—', answerText.length > 60 ? answerText.substring(0, 57) + '...' : answerText, status];
                });

                autoTable(doc, {
                    startY: y,
                    head: [['#', 'Question', 'Type', 'Answer', 'Result']],
                    body: tableRows,
                    margin: { left: 18, right: 18 },
                    styles: { fontSize: 7, cellPadding: 2, overflow: 'linebreak' },
                    headStyles: {
                        fillColor: COLORS.primary,
                        textColor: COLORS.white,
                        fontStyle: 'bold',
                    },
                    columnStyles: {
                        0: { cellWidth: 12 },
                        1: { cellWidth: 'auto' },
                        2: { cellWidth: 20 },
                        3: { cellWidth: 50 },
                        4: { cellWidth: 24 },
                    },
                    alternateRowStyles: { fillColor: [249, 250, 251] },
                });

                y = (doc as any).lastAutoTable.finalY + 8;
            }
        }
    }

    // ── Footer ───────────────────────────────────────────────
    const totalPages = doc.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setFontSize(7);
        doc.setTextColor(...COLORS.gray);
        doc.text(
            `HireHub Candidate Report — Page ${i} of ${totalPages}`,
            pageWidth / 2, doc.internal.pageSize.getHeight() - 8,
            { align: 'center' }
        );
    }

    // Save
    const safeName = (candidate.name || 'candidate').replace(/[^a-zA-Z0-9]/g, '_');
    doc.save(`HireHub_Report_${safeName}.pdf`);
}

// ── All Candidates Summary PDF ───────────────────────────────

export function generateAllCandidatesPDF(candidates: any[], assessmentName?: string, jobTitle?: string) {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();

    let y = addHeader(doc, assessmentName || 'Assessment Results', jobTitle || '');

    // Summary stats
    y = addSectionTitle(doc, y, 'Summary');
    y = addInfoRow(doc, y, 'Total Candidates', String(candidates.length));

    const completed = candidates.filter((c: any) => c.status === 'completed');
    y = addInfoRow(doc, y, 'Completed', String(completed.length));

    const avgScore = completed.length > 0
        ? completed.reduce((sum: number, c: any) => sum + (c.scorePercent ?? 0), 0) / completed.length
        : 0;
    y = addInfoRow(doc, y, 'Average Score', `${avgScore.toFixed(1)}%`);
    y += 6;

    // Candidates Ranking Table
    y = addSectionTitle(doc, y, 'Candidates Ranking');

    const sortedCandidates = [...candidates].sort((a, b) => (b.scorePercent ?? 0) - (a.scorePercent ?? 0));

    const rows = sortedCandidates.map((c: any, i: number) => {
        const score = c.scorePercent ?? 0;
        return [
            String(i + 1),
            c.candidateName || c.name || 'Unknown',
            c.email || '—',
            c.status || '—',
            c.mcqScore != null ? `${c.mcqScore.toFixed(0)}%` : '—',
            c.subjectiveScore != null ? `${c.subjectiveScore.toFixed(0)}%` : '—',
            `${score.toFixed(0)}%`,
            c.completedAt ? new Date(c.completedAt).toLocaleDateString('en-IN') : '—',
        ];
    });

    autoTable(doc, {
        startY: y,
        head: [['Rank', 'Name', 'Email', 'Status', 'MCQ', 'Subjective', 'Composite', 'Completed']],
        body: rows,
        margin: { left: 14, right: 14 },
        styles: { fontSize: 7, cellPadding: 3 },
        headStyles: {
            fillColor: COLORS.primary,
            textColor: COLORS.white,
            fontStyle: 'bold',
            fontSize: 7,
        },
        columnStyles: {
            0: { cellWidth: 12, halign: 'center' },
            1: { cellWidth: 'auto' },
            2: { cellWidth: 40 },
            3: { cellWidth: 18 },
            4: { cellWidth: 16, halign: 'center' },
            5: { cellWidth: 20, halign: 'center' },
            6: { cellWidth: 22, halign: 'center', fontStyle: 'bold' },
            7: { cellWidth: 22 },
        },
        alternateRowStyles: { fillColor: [249, 250, 251] },
        didDrawCell: (data) => {
            // Highlight composite score cells with color
            if (data.section === 'body' && data.column.index === 6) {
                const score = parseFloat(data.cell.text?.[0] || '0');
                const color = getScoreColor(score);
                doc.setTextColor(...color);
            }
        },
        didParseCell: (data) => {
            if (data.section === 'body' && data.column.index === 6) {
                const score = parseFloat(data.cell.text?.[0] || '0');
                if (score >= 70) data.cell.styles.textColor = COLORS.success;
                else if (score >= 40) data.cell.styles.textColor = COLORS.warning;
                else data.cell.styles.textColor = COLORS.danger;
            }
        },
    });

    y = (doc as any).lastAutoTable.finalY + 10;

    // Per-candidate detail pages
    for (const candidate of sortedCandidates) {
        const responses = candidate.responses || [];
        if (responses.length === 0) continue;

        doc.addPage();
        y = 20;

        const scorePercent = candidate.scorePercent ?? 0;
        const scoreColor = getScoreColor(scorePercent);

        // Candidate detail header
        doc.setFillColor(...COLORS.primary);
        doc.rect(0, 0, pageWidth, 22, 'F');
        doc.setTextColor(...COLORS.white);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(12);
        doc.text(candidate.candidateName || candidate.name || 'Unknown', 14, 10);
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.text(candidate.email || '', 14, 17);

        // Score badge on right
        doc.setFillColor(...scoreColor);
        doc.roundedRect(pageWidth - 42, 4, 30, 14, 3, 3, 'F');
        doc.setTextColor(...COLORS.white);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(11);
        doc.text(`${scorePercent.toFixed(0)}%`, pageWidth - 27, 13, { align: 'center' });
        doc.setTextColor(...COLORS.dark);

        y = 30;

        // Breakdown info
        if (candidate.breakdown?.mcqTotal > 0) {
            doc.setFontSize(8);
            doc.setFont('helvetica', 'normal');
            doc.text(`MCQ: ${candidate.breakdown.mcqCorrect}/${candidate.breakdown.mcqTotal} (${candidate.mcqScore?.toFixed(0) ?? '—'}%)  |  Subjective: ${candidate.subjectiveScore?.toFixed(0) ?? '—'}%  |  Time: ${candidate.timeUsedSeconds ? Math.round(candidate.timeUsedSeconds / 60) + ' min' : '—'}`, 14, y);
            y += 8;
        }

        // Question-by-question table
        const tableRows = responses.map((resp: any, i: number) => {
            let answerText = '';
            let status = '';

            if (resp.questionType === 'mcq') {
                const selected = resp.answer?.selectedOptionIds || [];
                const correct = resp.correctAnswer?.correctOptionIds || [];
                const options = resp.correctAnswer?.options || [];
                const selectedTexts = options.filter((o: any) => selected.includes(o.id)).map((o: any) => o.text);
                answerText = selectedTexts.join(', ') || 'No answer';
                if (correct.length > 0) {
                    status = JSON.stringify([...selected].sort()) === JSON.stringify([...correct].sort()) ? '✓' : '✗';
                }
            } else if (resp.questionType === 'speaking') {
                answerText = resp.answer?.videoKey ? 'Video' : 'None';
                status = resp.subjectiveScore ? `${resp.subjectiveScore.score}/${resp.subjectiveScore.maxScore}` : '—';
            } else {
                const answer = resp.answer;
                answerText = typeof answer === 'object'
                    ? (answer?.code || answer?.text || answer?.answer || 'Submitted').substring(0, 80)
                    : String(answer || 'N/A').substring(0, 80);
                status = resp.subjectiveScore ? `${resp.subjectiveScore.score}/${resp.subjectiveScore.maxScore}` : '—';
            }

            return [
                `Q${i + 1}`,
                (resp.questionText || resp.questionType || '—').substring(0, 80),
                resp.questionType?.toUpperCase() || '—',
                answerText.substring(0, 60),
                status,
            ];
        });

        autoTable(doc, {
            startY: y,
            head: [['#', 'Question', 'Type', 'Answer', 'Result']],
            body: tableRows,
            margin: { left: 14, right: 14 },
            styles: { fontSize: 7, cellPadding: 2, overflow: 'linebreak' },
            headStyles: {
                fillColor: COLORS.primary,
                textColor: COLORS.white,
                fontStyle: 'bold',
            },
            columnStyles: {
                0: { cellWidth: 12 },
                1: { cellWidth: 'auto' },
                2: { cellWidth: 20 },
                3: { cellWidth: 50 },
                4: { cellWidth: 20 },
            },
            alternateRowStyles: { fillColor: [249, 250, 251] },
        });
    }

    // Footers
    const totalPages = doc.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setFontSize(7);
        doc.setTextColor(...COLORS.gray);
        doc.text(
            `HireHub Assessment Report — Page ${i} of ${totalPages}`,
            pageWidth / 2, doc.internal.pageSize.getHeight() - 8,
            { align: 'center' }
        );
    }

    const safeName = (assessmentName || 'assessment').replace(/[^a-zA-Z0-9]/g, '_');
    doc.save(`HireHub_Results_${safeName}.pdf`);
}
